/**
 * Step 3: Enter credentials for the chosen platform.
 *
 *   MT5 / MT4:        login + password
 *   Match Trader:     email + password
 *   TradeLocker:      email + password + server (server is REQUIRED)
 *
 * No client-side cred validation beyond presence — real validation
 * happens on the bot in step 4.
 */

import { useEffect, useId, useState, type ReactNode } from "react";
import { motion } from "motion/react";
import { ShieldCheck } from "@/components/ui/icons";
import { CoolIcon } from "@/components/ui/cool-icon";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FirmAvatar } from "@/components/firm-logo";
import { firmLogoUrl } from "@/lib/firm-logos";
import type { FirmRow } from "@/lib/api/firms";
import type { PlatformType } from "@pfh/shared";
import type { AccountPhase, WizardState } from "./types";

interface Props {
  state: WizardState;
  onChange: (patch: Partial<WizardState>) => void;
}

export function StepCredentials({ state, onChange }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const isTradeLocker = state.platform === "tradelocker";
  const isMatchTrader = state.platform === "match-trader";
  const loginLabel = isTradeLocker || isMatchTrader ? "Email Address" : "Account Login";
  const loginId = useId();
  const passwordId = useId();
  const serverId = useId();
  const accountLabelId = useId();
  const loginUrlId = useId();

  // Prefer a per-platform URL seeded in firm_platforms; fall back to
  // the firm-level column for older seeds. Mirrors the bot's COALESCE
  // precedence so "bot can log in" ⇔ "wizard shows prefilled URL".
  const firmDefaultUrl =
    state.firm && state.platform
      ? resolveFirmTerminalUrl(state.firm, state.platform, state.phase)
      : "";
  const hasFirmDefault = firmDefaultUrl.length > 0;

  // Credentials are wiped from local state when the user backs out so
  // they don't linger in memory between firm changes.
  useEffect(() => {
    return () => {
      onChange({ login: "", password: "", server: "", accountLabel: "", loginUrl: "" });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const firmLogo = state.firm ? firmLogoUrl(state.firm.id) ?? "" : "";

  return (
    <div className="space-y-5">
      {/* Firm reassurance banner — confirms which firm we're logging into
          and reminds the user this is read-only. */}
      {state.firm && (
        <Alert className="flex items-center gap-3 border-border/50 bg-muted/20 py-3 pr-3 pl-3">
          <FirmAvatar
            src={firmLogo}
            name={state.firm.name}
            size="sm"
            className="shrink-0"
          />
          <AlertDescription className="flex-1 text-xs leading-relaxed text-muted-foreground">
            Logging into <span className="font-medium text-foreground">{state.firm.name}</span>{" "}
            read-only to verify your balance.{" "}
            <span className="inline-flex items-center gap-1 whitespace-nowrap font-medium text-emerald-500">
              <ShieldCheck className="h-3 w-3" />
              No trades placed
            </span>
            .
          </AlertDescription>
        </Alert>
      )}

      {/* Credential panel — nested card feels like a proper data-entry
          surface instead of fields floating in the dialog. */}
      <div className="space-y-4 rounded-xl border border-border/40 bg-muted/10 p-5">
        <CredField
          id={loginId}
          label={loginLabel}
          value={state.login}
          onChange={(v) => onChange({ login: v })}
          type={isTradeLocker || isMatchTrader ? "email" : "text"}
          placeholder={isTradeLocker || isMatchTrader ? "you@example.com" : "12345678"}
          autoFocus
          autoComplete="username"
        />

        <CredField
          id={passwordId}
          label="Password"
          value={state.password}
          onChange={(v) => onChange({ password: v })}
          type={showPassword ? "text" : "password"}
          autoComplete="current-password"
          placeholder="••••••••"
          className="pr-11"
          endAdornment={
            <motion.button
              type="button"
              aria-label={showPassword ? "Hide Password" : "Show Password"}
              aria-pressed={showPassword}
              onClick={() => setShowPassword((v) => !v)}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.94 }}
              transition={{ duration: 0.15 }}
              className="absolute right-1.5 top-1.5 inline-flex h-7 w-8 items-center justify-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-accent/10 hover:text-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <CoolIcon name={showPassword ? "Hide" : "Show"} size="1rem" />
            </motion.button>
          }
        />

        {isTradeLocker && (
          <CredField
            id={serverId}
            label="Server"
            hint="Find this in your TradeLocker welcome email"
            value={state.server}
            onChange={(v) => onChange({ server: v })}
            type="text"
            placeholder="e.g. BLBRY"
          />
        )}

        {isMatchTrader && (
          <CredField
            id={accountLabelId}
            label="Account Name or Number"
            hint="Your Match Trader dashboard lists every account you have — we need the exact label or number of the one we should trade"
            value={state.accountLabel}
            onChange={(v) => onChange({ accountLabel: v })}
            type="text"
            placeholder="e.g. Challenge $10,000 or 1234567"
          />
        )}

        <CredField
          id={loginUrlId}
          label={hasFirmDefault ? "Terminal URL (Optional Override)" : "Terminal URL"}
          hint={
            hasFirmDefault
              ? "Leave blank to use the firm default"
              : "No default on file — paste the terminal URL"
          }
          value={state.loginUrl}
          onChange={(v) => onChange({ loginUrl: v })}
          type="url"
          placeholder={hasFirmDefault ? firmDefaultUrl : "https://platform.example.com/login"}
          autoComplete="off"
        />
      </div>

      <p className="text-[11px] leading-relaxed text-muted-foreground">
        Heads up: 2-factor authentication isn&rsquo;t supported yet. Accounts with 2FA enabled
        will time out after about 30 seconds — try an account without 2FA, or disable it
        temporarily.
      </p>
    </div>
  );
}

/**
 * A labeled input with the small uppercase label style used throughout
 * the dashboard (overview stats, objective cards). Keeps the form rhythm
 * tight and branded.
 */
function CredField({
  id,
  label,
  hint,
  value,
  onChange,
  type = "text",
  placeholder,
  autoFocus,
  autoComplete,
  className,
  endAdornment,
}: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  type?: React.HTMLInputTypeAttribute;
  placeholder?: string;
  autoFocus?: boolean;
  autoComplete?: string;
  className?: string;
  endAdornment?: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <label
          htmlFor={id}
          className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
        >
          {label}
        </label>
        {hint && <span className="text-[10px] text-muted-foreground/80">{hint}</span>}
      </div>
      <div className="relative">
        <Input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          className={`h-10 px-3.5 text-sm ${className ?? ""}`}
        />
        {endAdornment}
      </div>
    </div>
  );
}

// MIRROR: apps/worker/src/routes/api/accounts.ts:74-83. The bot's
// /sync endpoint resolves a bot-ready login URL in this same
// precedence; keep the two in lockstep so "bot can log in" ⇔ "wizard
// shows a prefilled default". step1/step2/blown all route through the
// demo branch — the bot does the same CASE split.
function resolveFirmTerminalUrl(
  firm: FirmRow,
  platform: PlatformType,
  phase: AccountPhase,
): string {
  const fp = firm.platforms?.find((p) => p.platform_type === platform) ?? null;
  const isFunded = phase === "funded";
  const phaseFp = isFunded ? fp?.terminal_url_funded : fp?.terminal_url_demo;
  const phaseF = isFunded ? firm.terminal_url_funded : firm.terminal_url_demo;
  const candidates = [
    fp?.login_url,
    phaseFp,
    phaseF,
    fp?.terminal_url,
    firm.terminal_url,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.length > 0) return c;
  }
  return "";
}
